import React, {
  FC,
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useEffect,
} from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { ModalText, InputError } from '../Typography'
import { ModalButton } from '../ModalButton'
import { color } from '../../styles'
import { City } from '../../store/models/user'
import { useDispatch } from '../../store'
import { Modal } from '../Modal'

import {
  Wrap,
  StyledForm,
  StyledInput,
  SelectButton,
  LocateButton,
} from './styled'

import { useDebounce } from 'src/hooks/useDebounce'
import { getCity } from 'src/api/cities/cities'

type Props = {
  setOpenCity: Dispatch<SetStateAction<boolean>>
  setSelectedCity: Dispatch<SetStateAction<City | undefined>>
  openCity: boolean
}

type Coords = {
  lat: number
  lng: number
}

const locationOptions = {
  enableHighAccuracy: true,
  timeout: 5000,
}

export const CitySelect: FC<Props> = ({
  openCity,
  setOpenCity,
  setSelectedCity,
}) => {
  const formikCity = useFormik({
    initialValues: {
      city: '',
    },
    validationSchema: Yup.object({
      city: Yup.string(),
    }),
    onSubmit: () => {},
  })
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [cities, setCities] = useState<City[]>()
  const [cityChosen, setCityChosen] = useState<City | undefined>()
  const [coords, setCoords] = useState<Coords>()
  const [prevCoords, setPrevCoords] = useState<Coords>()
  const [positionError, setPositionError] = useState(false)
  const debouncedSearchCity = useDebounce(formikCity.values.city, 500)

  const handleSelectCity = useCallback(() => {
    setSelectedCity(cityChosen)
    if (cities) {
      dispatch.user.updateUser({ city: cityChosen })
    }
    setOpenCity(false)
  }, [setSelectedCity, dispatch, setOpenCity, cityChosen, cities])

  const locationSuccess = useCallback(
    (pos: Position) => {
      const newCoords: Coords = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      }
      setCoords((oldCoords) => {
        if (oldCoords !== newCoords) {
          setPrevCoords(oldCoords)
          return newCoords
        }
        return prevCoords
      })
    },
    [prevCoords]
  )

  const locationError = useCallback(() => {
    setPositionError(true)
    setLoading(false)
  }, [])

  const getLocation = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      locationSuccess,
      locationError,
      locationOptions
    )
  }, [locationSuccess, locationError])

  const locateUser = useCallback(() => {
    setLoading(true)
    formikCity.resetForm()
    getLocation()
  }, [formikCity, getLocation])

  useEffect(() => {
    const fetchByCoords = async () => {
      if (prevCoords !== coords) {
        try {
          setLoading(true)
          const response = await getCity(
            `?lat=${String(coords?.lat)}&lng=${String(coords?.lng)}`
          )
          setCities(response)
          setLoading(false)
        } catch (error) {
          setPositionError(true)
        }
      }
    }
    fetchByCoords()
  }, [coords, prevCoords])

  useEffect(() => {
    const debouncedSearch = async () => {
      if (debouncedSearchCity) {
        try {
          setLoading(true)
          const response = await getCity(`?city=${debouncedSearchCity}`)
          setCities(response)
          setLoading(false)
        } catch (error) {
          setPositionError(true)
        }
      }
    }
    debouncedSearch()
  }, [debouncedSearchCity])

  return (
    <Modal isOpen={openCity} onClose={() => setOpenCity(false)}>
      <Wrap>
        <ModalText>Select City</ModalText>
        <LocateButton onClick={locateUser}>Locate me</LocateButton>
        {positionError && (
          <InputError>Not able to get your location.</InputError>
        )}
        <StyledForm onSubmit={formikCity.handleSubmit}>
          <StyledInput
            id="city"
            name="city"
            type="text"
            placeholder="Los Angeles, CA"
            onChange={formikCity.handleChange}
            value={formikCity.values.city}
          />
          {cities &&
            cities.map((city, index) => (
              <SelectButton
                key={String(index)}
                isActive={
                  cityChosen?.name === city.name &&
                  cityChosen?.country === city.country &&
                  cityChosen?.state === city.state
                }
                onClick={() => setCityChosen(city)}
              >
                {city.name}, {city.country}
              </SelectButton>
            ))}
        </StyledForm>
        <ModalButton
          color={color.DUSTY_ORANGE}
          label="Select City"
          disabled={cityChosen ? false : true}
          onClick={() => handleSelectCity()}
          loading={loading}
          city
        />
      </Wrap>
    </Modal>
  )
}
